import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IRadioBoxProps } from 'shared/ui/Radio'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { EnumUserRoles, type IUser, type Permissions } from 'entities/Users/model/types'
import { UsersApi } from 'entities/Users'
import { User } from 'entities/Users/model/User'
import { type IResponseUser } from 'entities/Users/api/types'
import { DowngradeMemberModal } from 'pages/settings/pages/members/ui/actions/downgradeMember/ui/DowngradeMemberModal/DowngradeMemberModal'

type SeatOption = 'keep' | 'remove'

export class DowngradeMemberStore {
  private _modalId = nanoid()
  private _deleteSeatOption: SeatOption = 'keep'
  private _reassignContactsUserId: number | null = null
  private _reassignConversationsUserId: number | null = null
  private _reassignInboxOwnershipUserId: number | null = null
  private _disposeReaction: IReactionDisposer | null = null

  constructor(
    private _memberToDowngrade: IUser,
    private _entities: IEntitiesStore,
    private _permissions: Permissions,
    private _useOrganizationSettings: boolean,
    private _onSuccess?: (member: IResponseUser) => void | Promise<void>
  ) {
    makeAutoObservable(this)
    this._permissions = _permissions
    this._useOrganizationSettings = _useOrganizationSettings
    this.openDowngradeMemberModal()
  }

  openDowngradeMemberModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Downgrade',
      type: ModalTypeList.DEFAULT,
      ModalContent: DowngradeMemberModal,
      ModalContentProps: {
        store: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onCloseDowngradeMemberModal,
      primaryAction: {
        text: 'Downgrade',
        disabled: this.downgradeIsDisabled,
        onAction: this.onDowngradeMember,
      },
    })

    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => this.downgradeIsDisabled,
      (isDisabled) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = isDisabled
        }
      }
    )
  }

  onCloseDowngradeMemberModal = () => {
    this._disposeReaction?.()
    modalStore.removeModal(this._modalId)
  }

  onSetSeatOption = (option: SeatOption) => {
    this._deleteSeatOption = option
  }

  onSetReassignContactsUserId = (userId: number) => {
    this._reassignContactsUserId = userId
  }

  onSetReassignConversationsUserId = (userId: number) => {
    this._reassignConversationsUserId = userId
  }

  onSetReassignInboxOwnershipUserId = (userId: number) => {
    this._reassignInboxOwnershipUserId = userId
  }

  onDowngradeMember = async () => {
    const memberToDowngrade = this._memberToDowngrade

    if (!memberToDowngrade || !this._permissions) return

    try {
      const { data } = await UsersApi.updateMember(memberToDowngrade.id, {
        reassign_contacts_user_id: this._reassignContactsUserId ?? undefined,
        reassign_conversations_user_id: this._reassignConversationsUserId ?? undefined,
        reassign_teams_user_id: this._reassignInboxOwnershipUserId ?? undefined,
        permissions: this._permissions,
        remove_seat: this._deleteSeatOption === 'remove' ? 1 : 0,
        role: EnumUserRoles.VIEW_ONLY,
        use_organization_settings: this._useOrganizationSettings,
      })

      toastStore.add({
        type: 'success',
        title: 'Permissions updated',
      })
      if (data && memberToDowngrade instanceof User) {
        memberToDowngrade.syncOrigin(data)
        this._onSuccess?.(data)
      }

      this.onCloseDowngradeMemberModal()
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to update permissions. Please try again',
      })
    }
  }

  get availableMembersForReassignment() {
    return this._entities.usersStore.users.filter(
      (user) => user.id !== this._memberToDowngrade?.id && user.role !== EnumUserRoles.VIEW_ONLY
    )
  }

  get memberToDowngrade() {
    return this._memberToDowngrade
  }

  get hasPaidSeat() {
    return this._memberToDowngrade?.role !== EnumUserRoles.VIEW_ONLY
  }

  get hasInboxOwnership() {
    return this._memberToDowngrade?.origin.is_owned_teams ?? false
  }

  get deleteSeatOption() {
    return this._deleteSeatOption
  }

  get reassignContactsUserId() {
    return this._reassignContactsUserId
  }

  get reassignConversationsUserId() {
    return this._reassignConversationsUserId
  }

  get reassignInboxOwnershipUserId() {
    return this._reassignInboxOwnershipUserId
  }

  get seatOptions(): IRadioBoxProps<SeatOption>[] {
    return [
      {
        id: 'keep',
        value: 'keep',
        text: 'Keep seat',
      },
      {
        id: 'remove',
        value: 'remove',
        text: 'Remove seat',
      },
    ]
  }

  get downgradeIsDisabled() {
    const needsReassignment = this.hasPaidSeat

    if (needsReassignment) {
      if (!this._reassignContactsUserId) return true
      if (!this._reassignConversationsUserId) return true
      if (this.hasInboxOwnership && !this._reassignInboxOwnershipUserId) {
        return true
      }
    }

    return false
  }
}
